import { useMarketIntelligencePermissions } from '@/hooks/permissions/marketIntelligence/useMarketIntelligencePermissions'
import { useToken } from '@/hooks/useToken'
import { MarketIntelligencePages } from '@/routes/MarketIntelligencePages'
import { PATHS } from '@/routes/PATHS'
import { CoreLevelFeatureNames } from '@/services/types/permissions/permissionFeatures/mockCorePermissionFeature'
import { PremiumFeatures } from '@/services/types/premiumFeatures/premiumFeatures'
import { useCorePermissions } from '@/stores/permissions/permissions'
import { usePremiumFeaturesPermissions } from '@/stores/permissions/premiumFeatures'
import { AlertOutlined, FireOutlined, PieChartOutlined, SearchOutlined } from '@ant-design/icons'
import { Lightbulb } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export function useAnalyticsSidebarItems() {
  const { token } = useToken()
  const { t } = useTranslation()
  const { shouldShowMarketIntelligencePages } = useMarketIntelligencePermissions()

  const reportsPermission = useCorePermissions(CoreLevelFeatureNames.emailedReports)
  const listingsManagementPermission = useCorePermissions(CoreLevelFeatureNames.listingsManagement)

  const premiumFeaturesPermissions = usePremiumFeaturesPermissions()
  const hasListingsFeature = premiumFeaturesPermissions.includes(PremiumFeatures.Listings)
  const hasReviewMonitoringFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.ReviewMonitoring,
  )

  const hasReportsPermission = !!reportsPermission?.edit
  const shouldHideListingsPageItem = !!listingsManagementPermission?.hide
  const hideListingsPageItem = shouldHideListingsPageItem || !hasListingsFeature
  const hasReportsFeature = hasListingsFeature || hasReviewMonitoringFeature

  const allItems = [
    {
      key: PATHS.analyticsPage,
      icon: <PieChartOutlined />,
      label: t('sidebarItems.customerInteractions'),
    },
    {
      key: PATHS.searchAppearancesPage,
      icon: <SearchOutlined />,
      label: t('sidebarItems.searchAppearances'),
    },
    {
      key: PATHS.reviewAnalysis,
      icon: <FireOutlined />,
      label: t('sidebarItems.reviewsInsights'),
    },
    {
      key: PATHS[MarketIntelligencePages.locationsTable],
      icon: <Lightbulb size={token.fontSize} />,
      label: t('sidebarItems.marketIntelligence'),
    },
    {
      key: PATHS.emailReportsPage,
      icon: <AlertOutlined />,
      label: t('sidebarItems.reports'),
    },
  ]

  return allItems
    .filter((item) =>
      hasReportsPermission && hasReportsFeature ? true : item.key !== PATHS.emailReportsPage,
    )
    .filter((item) => (hasReviewMonitoringFeature ? true : item.key !== PATHS.reviewAnalysis))
    .filter((item) =>
      shouldShowMarketIntelligencePages
        ? true
        : item.key !== PATHS[MarketIntelligencePages.locationsTable],
    )
    .filter((item) => (hideListingsPageItem ? item.key !== PATHS.analyticsPage : true))
    .filter((item) => (hideListingsPageItem ? item.key !== PATHS.searchAppearancesPage : true))
}
